import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FolderKanban, ChevronRight, AlertTriangle, CheckCircle, Activity } from "lucide-react";
import { cn } from "@/lib/utils";
import { SectionHelp } from "@/components/help";

interface ProjectHealth {
  projectId: number;
  projectName: string;
  progress: number;
  health: "green" | "yellow" | "red";
  overdueMilestones: number;
  atRiskMilestones: number;
}

interface ProjectHealthWidgetProps {
  projects: ProjectHealth[];
}

export default function ProjectHealthWidget({ projects }: ProjectHealthWidgetProps) {
  const [, navigate] = useLocation();

  if (projects.length === 0) {
    return (
      <Card className="h-full border-0 shadow-lg bg-card">
        <CardHeader className="pb-3 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 shadow-md">
              <Activity className="h-5 w-5 text-white" />
            </div>
            <SectionHelp contentId="dashboard:project-health" tooltip="Learn about project health">
              <CardTitle className="text-lg font-bold">Project Health</CardTitle>
            </SectionHelp>
          </div>
        </CardHeader>
        <CardContent className="p-5">
          <div className="py-8 text-center">
            <div className="w-12 h-12 bg-muted rounded-xl flex items-center justify-center mx-auto mb-3">
              <FolderKanban className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground font-medium text-sm">No active projects</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getHealthStyles = (health: string) => {
    switch (health) {
      case "green": return { 
        dot: "bg-emerald-500", 
        bg: "bg-emerald-50 dark:bg-emerald-950/30",
        progress: "from-emerald-400 to-emerald-600",
        icon: <CheckCircle className="h-4 w-4 text-emerald-500" />
      };
      case "yellow": return { 
        dot: "bg-amber-500", 
        bg: "bg-amber-50 dark:bg-amber-950/30",
        progress: "from-amber-400 to-amber-600",
        icon: <AlertTriangle className="h-4 w-4 text-amber-500" />
      };
      case "red": return { 
        dot: "bg-red-500", 
        bg: "bg-red-50 dark:bg-red-950/30",
        progress: "from-red-400 to-red-600",
        icon: <AlertTriangle className="h-4 w-4 text-red-500" />
      };
      default: return { 
        dot: "bg-muted-foreground/50",
        bg: "bg-muted", 
        progress: "from-muted-foreground/50 to-muted-foreground/80",
        icon: null 
      };
    }
  };

  return (
    <Card className="h-full border-0 shadow-lg bg-card">
      <CardHeader className="pb-3 border-b border-border flex flex-row items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 shadow-md">
            <Activity className="h-5 w-5 text-white" />
          </div>
          <CardTitle className="text-lg font-bold">Project Health</CardTitle>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          className="text-violet-600 hover:text-violet-700 hover:bg-violet-50 dark:hover:bg-violet-950/30 gap-1 font-semibold h-8 px-2"
          onClick={() => navigate("/projects")}
        >
          View <ChevronRight className="h-3 w-3" />
        </Button>
      </CardHeader>
      <CardContent className="p-4">
        <div className="space-y-3">
          {projects.slice(0, 5).map((p) => {
            const styles = getHealthStyles(p.health);
            return (
              <div 
                key={p.projectId}
                className={cn(
                  "flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all duration-200",
                  "bg-card border border-border",
                  "hover:shadow-sm hover:border-violet-100 dark:hover:border-violet-800"
                )}
                onClick={() => navigate(`/projects/${p.projectId}`)}
              >
                <div className={cn("w-2.5 h-2.5 rounded-full flex-shrink-0", styles.dot)} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-foreground text-sm truncate">{p.projectName}</span>
                    {styles.icon}
                  </div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div 
                        className={cn("h-full rounded-full bg-gradient-to-r", styles.progress)}
                        style={{ width: `${p.progress}%` }}
                      />
                    </div>
                    <span className="text-xs font-bold text-muted-foreground w-8 text-right">{p.progress}%</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {projects.length > 5 && (
          <div className="mt-4 text-center">
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-violet-600 hover:text-violet-700 hover:bg-violet-50 dark:hover:bg-violet-950/30 font-semibold"
              onClick={() => navigate("/projects")}
            >
              View {projects.length - 5} more projects
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
